import React from 'react';

import {
  AddCircle as AddCircleIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import {
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  styled,
  Box,
  Stack,
  Button
} from '@mui/material';
import _ from 'lodash';
import {
  activityTemplates,
  AuthWrapper,
  DialogModal,
  FundraiseCard,
  LoadingOverlay,
  useDialogControl,
  BackButton,
  appEvents
} from 'modules';
import {
  useNavigate,
  useParams
} from 'react-router-dom';

import * as dataService from './dataService';
import { FundraiseCampaignStageColumn } from './FundraiseCampaignStageColumn/FundraiseCampaignStageColumn';



const ColumnContainer = styled(Box)({
  minWidth: 310,
  display: 'flex',
  alignItems: 'stretch',
  marginBottom: 80
});

const BoardContainer = styled(Stack)(({ theme }) => ({
  overflowX: 'scroll',
  minHeight: 750
}));

const Flipped = styled(Box)({
  transform: 'rotateX(180deg)',
  display: 'flex'
});


export const FundraiseActivity: React.FC = props => {
  const params = useParams<{id: string}>();
  const navigate = useNavigate();

  const { loading, data, mutations, refetch } = dataService.useFundraiseActivity(params.id ?? '');
  const [
    fnWrappedSaveInvestorCardMutation,
    saveInvestorCardMutationTuple
  ] = dataService.useSaveInvestorCardMutation();

  const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
  const markAsCompleteDialogControl = useDialogControl({});
  const removeFundraiseDialogControl = useDialogControl({});

  const [expandAll, setExpandAll] = React.useState(false);



  const handleMarkAsComplete = React.useCallback(() => {
    setAnchorEl(null);
    markAsCompleteDialogControl.openDialog();
  }, [markAsCompleteDialogControl]);

  const handleReopenFundraise = React.useCallback(async () => {
    setAnchorEl(null);
    await mutations.reopenCampaign();
    await refetch();
  }, [mutations, refetch]);

  const handleRemoveFundraise = React.useCallback(async () => {
    setAnchorEl(null);
    removeFundraiseDialogControl.openDialog();
  }, [removeFundraiseDialogControl]);



  function handleClickCreateNewCard() {
    appEvents.broadcast({
      type: 'modal',
      onClose: () => refetch(),
      args: {
        type: 'editInvestorCard',
        campaignId: params.id
      }
    });
  }



  function handleChangeExpandAll() {
    window.localStorage.setItem('isExpanded', 'false');
    setExpandAll(prevState => !prevState);
  }



  function handleEditFundraise() {
    setAnchorEl(null);
    appEvents.broadcast({
      type: 'modal',
      args: {
        type: 'editFundraisingCampaign',
        campaignId: params.id
      }
    });
  }



  return (
    <AuthWrapper.FC>
      <activityTemplates.BaseActivityTemplate.FC>
        <LoadingOverlay active={loading || saveInvestorCardMutationTuple.loading} />

        <BackButton.FC label="Back to Fundraises" />

        <Stack spacing={7}>
          <Stack
            direction="row"
            spacing={130/8}>
            <Box>
              <FundraiseCard.FC
                variant="detail"
                campaignSummary={data.campaign} />
            </Box>

            <Box flexGrow={1}>
              <Typography variant='h3'>Description</Typography>
              <Typography>{data.campaign.description}</Typography>
            </Box>

            <Box>
              <IconButton onClick={evt => setAnchorEl(evt.currentTarget)} >
                <MoreVertIcon />
              </IconButton>
            </Box>
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between">
            <FormControlLabel
              control={<Switch color="primary" />}
              onChange={handleChangeExpandAll}
              label="Expand All"
              labelPlacement="start" />

            <Button
              startIcon={<AddCircleIcon />}
              onClick={handleClickCreateNewCard}>
              Create New Card
            </Button>
          </Stack>

          <Flipped>
            <BoardContainer
              className='customScrollbar'
              direction="row"
              alignItems="stretch"
              spacing={3}>
              <Flipped>
                {_.map(data?.stages, stage => (
                  <ColumnContainer key={stage.name}>
                    <FundraiseCampaignStageColumn
                      expandAll={expandAll}
                      cards={data.stageName2InvestorCards[stage.name]}
                      campaignId={data.campaign.id}
                      favoritedInvestors={data.favoritedInvestors}
                      columnStage={stage.name}
                      stages={data.stages}
                      onSubmit={fnWrappedSaveInvestorCardMutation} />
                  </ColumnContainer>
                ))}
              </Flipped>
            </BoardContainer>
          </Flipped>
        </Stack>
      </activityTemplates.BaseActivityTemplate.FC>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleEditFundraise}>
          <Typography>
            Edit Fundraise
          </Typography>
        </MenuItem>

        {data.campaign.completedAt ? (
          <MenuItem onClick={handleReopenFundraise}>
            <Typography>
              Reopen Fundraise
            </Typography>
          </MenuItem>

        ) : (
          <MenuItem onClick={handleMarkAsComplete}>
            <Typography>
              Mark as Complete
            </Typography>
          </MenuItem>
        )}

        <MenuItem onClick={handleRemoveFundraise}>
          <Typography color='#EB5757'>
            Remove Fundraise
          </Typography>
        </MenuItem>
      </Menu>

      <DialogModal.FC
        control={markAsCompleteDialogControl}
        title='Are You Sure?'
        content={
          <Typography>
            Once you mark this fundraise as complete, you won't be able to edit or delete any content. If you wish to make changes in the future, you will have to reopen the fundraise.
          </Typography>
        }
        loading={loading}
        onConfirm={async () => {
          await mutations.markCampaignComplete();
          await refetch();
        }}
      />

      <DialogModal.FC
        control={removeFundraiseDialogControl}
        title='Are You Sure?'
        content={
          <Typography>
            Once you remove this fundraise, it cannot be undone. Please make sure if you would like to go through with this.
          </Typography>
        }
        loading={loading}
        onConfirm={async () => {
          await mutations.deleteCampaign();
          navigate(-1);
        }}
      />
    </AuthWrapper.FC>
  );
};
